import { apiClient } from './client';

// ─── Guide context (canton-personalized) ──────────────────────────────────────

export interface GuideAusgleichskasse {
  name: string;
  phone: string | null;
  website: string | null;
  householdPortalUrl: string | null;
  akNumber: number | null;
  notes: string | null;
}

export interface GuideRates {
  year: number;
  ahvIvEo: number;
  alv: number;
  fak: number | null;
  fakEmployee: number | null;
  fakNotes: string | null;
  alvSalaryCap: number;
}

export interface GuideThresholds {
  sackgeldAmount?: number;
  sackgeldAge?: number;
  retirementFreibetrag?: number;
  bvgEntry: number;
  vavPerEmployee?: number;
  vavTotalPayroll?: number;
  flatTaxRate?: number;
  // employee variant
  employeeContributionRate?: number;
  accidentHoursThreshold?: number;
}

export interface GuideVavEligibility {
  qualifiesNow: boolean;
  totalPayroll: number | null;
  recommendedProcedure: string | null;
  reason: string | null;
  warnings: string[];
}

export interface EmployerGuideContext {
  canton: string | null;
  ausgleichskasse: GuideAusgleichskasse | null;
  rates: GuideRates | null;
  thresholds: GuideThresholds;
  vavRestrictedForFrenchCrossBorder: boolean;
  vavEligibility: GuideVavEligibility | null;
  taxAuthorityUrl: string | null;
  simplifiedProcedure: { available?: boolean; registrationUrl?: string } | null;
  registration: {
    isRegistered: boolean;
    compensationOfficeId: string | null;
    compensationOfficeName: string | null;
  };
  nextDeadlines: Record<string, string> | null;
  autoDerived: Record<string, boolean>;
}

export interface EmployeeGuideContext {
  canton: string | null;
  hasAhvNumber: boolean;
  accidentInsuranceApplies: boolean;
  bvgApplies: boolean;
  taxationMethod: string | null;
  thresholds: GuideThresholds;
  autoDerived: Record<string, boolean>;
}

export type GuideContext = EmployerGuideContext | EmployeeGuideContext;

export interface GuideProgress {
  completedSteps: string[];
  dismissedAt: string | null;
  completedAt: string | null;
  autoDerived: Record<string, boolean>;
}

export interface GuideResponse {
  role: 'employer' | 'employee';
  context: GuideContext;
  progress: GuideProgress;
}

export interface UpdateGuideStepInput {
  stepId?: string;
  done?: boolean;
  dismissed?: boolean;
  completed?: boolean;
}

export const onboardingApi = {
  getGuide: async (role?: 'employer' | 'employee'): Promise<GuideResponse> => {
    const qs = role ? `?role=${role}` : '';
    const res = await apiClient.get<{ success: boolean; data: GuideResponse }>(
      `/onboarding/guide${qs}`
    );
    return res.data;
  },

  updateStep: async (
    input: UpdateGuideStepInput
  ): Promise<{ progress: Omit<GuideProgress, 'autoDerived'> }> => {
    const res = await apiClient.patch<{
      success: boolean;
      data: { progress: Omit<GuideProgress, 'autoDerived'> };
    }>('/onboarding/guide/step', input);
    return res.data;
  },
};
